<template lang="pug">
.needs-meals-tab(v-cloak)
  NeedsMealsTable.mt-3(
    v-model="items"
    :fields="fields"
    :totalRows.sync="rows"
    :currentPage="currentPage"
    :perPage="perPage"
    :isLoading="isLoading"
    :filter="filter"
    @updateCountTargetByGroupId="updateCountTargetByGroupId"
    @onDetail="onDetail"
    @onListDetail="onListDetail"
    :setting="setting"
  )
  .table-options.d-flex.flex-row.justify-content-end.align-items-center.flex-wrap
    //- フィルタリング
    .resident-group-filter.d-flex.align-items-center
      span.table-option-text 利用者グループ:
      .filter
        b-form-select(id="needs-meal-filter-input" v-model="filter.id" :options="filterOptions" size="md")
    //- ページあたりの行数
    span.table-option-text 1ページの行数:
    .per-page
      b-form-select(
        id="needs-meal-list-per-page-select"
        v-model="perPage"
        :options="pageOptions"
        size="md"
        @change="selectPerPage"
      )
    //- ページネーション
    b-pagination.m-0(
      v-model="currentPage"
      :total-rows="rows"
      :per-page="perPage"
      :disabled="isLoading"
      @change="selectPage"
      hide-goto-end-buttons
    )
  
  //- 食事キャンセル（日付、食事指定）モーダル
  NeedsMealModal(v-model="showMealModal" :mealType="needsMealModalMealType" :datetime="needsMealModalDatetime" :items="needsMealModalItems")
  //- 食事キャンセル（日付、全食事）モーダル
  NeedsMealsModal(v-model="showMealsModal" :datetime="needsMealsModalDatetime" :items="needsMealsModalItems" :setting="setting")
</template>

<script lang="ts">
import { Component, Inject, Watch, Vue } from "vue-property-decorator";
import { userStore, itemListFilterStore } from "@/store/store";
import { errorHandle } from "@/errorHandler";
import SystemError from "@/commons/models/SystemError";

import Setting from "../../../../public/setting.json";

import OutingsService, { OutingNeedsMealsGetModel } from "@/commons/services/Outings";
import OutingSettingsService, { OutingSettingGetModel } from "@/commons/services/OutingSettings";
import ResidentGroupsService, { ResidentGroupsGetModel } from "@/commons/services/ResidentGroups";

import HelpIcon from "@/pc/basics/HelpIcon.vue";
import NeedsMealsTable, { NeedsMealsTableField, NeedsMealsTableItem, FilterSetting, MealType, TableItemNeedsMeal } from "@/pc/containers/outings/NeedsMealsTable.vue";
import NeedsMealModal from "@/pc/containers/outings/NeedsMealModal.vue";
import { NeedsMealModalTableItem } from "@/pc/containers/outings/NeedsMealModalTable.vue";
import NeedsMealsModal from "@/pc/containers/outings/NeedsMealsModal.vue";
import { NeedsMealsModalTableItem } from "@/pc/containers/outings/NeedsMealsModalTable.vue";

@Component({
  components: {
    HelpIcon,
    NeedsMealsTable,
    NeedsMealModal,
    NeedsMealsModal
  }
})
export default class NeedsMealsTab extends Vue {
  /** 外出外泊サービス */
  @Inject("OutingsService") outingsService!: OutingsService;
  /** 外出外泊設定サービス */
  @Inject("OutingSettingsService") outingSettingsService!: OutingSettingsService;
  /** 利用者グループサービス */
  @Inject("ResidentGroupsService") residentGroupsService!: ResidentGroupsService;

  /** 外出外泊届け設定 */
  setting: OutingSettingGetModel | null = null;

  /** テーブル列名 */
  fields: Array<NeedsMealsTableField> = [];
  /** テーブル各行の情報 */
  items: Array<NeedsMealsTableItem> = [];
  /** テーブルの「選択」列のチェックボックス値 */
  allSelects: boolean[] = [];
  /** 読込み中か否か */
  isLoading: boolean = true;
  /** フィルタリング条件 */
  filter: FilterSetting = { id: "" };
  /** フィルター選択項目 */
  filterOptions: Array<{ value: string; text: string }> = [];
  /** ページングの現在ページ番号 */
  currentPage: number = 1;
  /** ページングの全体行数 */
  rows: number = 0;
  /** 1ページに表示する行数 */
  perPage: number = Number(Setting.outings.needsMeals.perPage);
  /** 1ページに表示する行数の選択項目 */
  pageOptions: number[] = Setting.outings.needsMeals.pageOptions;

  /** 利用者グループMAP */
  groupIdMap: Map<number, ResidentGroupsGetModel> = new Map<number, ResidentGroupsGetModel>();

  /** 食事キャンセル（日付、食事指定）モーダル表示可否 */
  showMealModal: boolean = false;
  /** 食事キャンセル（日付、食事指定）モーダル日付 */
  needsMealModalDatetime: number | null = null;
  /** 食事キャンセル（日付、食事指定）モーダル食事種別 */
  needsMealModalMealType: MealType = MealType.BREAKFAST;
  /** 食事キャンセル（日付、食事指定）モーダル表示対象 */
  needsMealModalItems: Array<NeedsMealModalTableItem> = [];

  /** 食事キャンセル（日付、全食事）モーダル表示可否 */
  showMealsModal: boolean = false;
  /** 食事キャンセル（日付、全食事）モーダル日付 */
  needsMealsModalDatetime: number | null = null;
  /** 食事キャンセル（日付、全食事）モーダル表示対象 */
  needsMealsModalItems: Array<NeedsMealsModalTableItem> = [];

  /**
   * ### 1ページあたりの表示行数の変更イベント
   *
   * - 変更をクッキーに持たせる
   */
  @Watch("perPage")
  public changePerPage(perPage: number): void {
    this.setCookie(perPage);
    return;
  }

  /**
   * ### 1ページあたりの表示行数をCookieに設定する
   */
  public setCookie(perPage: number): void {
    // Cookie有効秒数。有効期限は必ず設定しないといけないので、10年分にしておく
    const saveSecond = 10 * 365 * 24 * 60 * 60;
    document.cookie = `needs-meals-list-per-page=${perPage}; max-age=${saveSecond};`;
    return;
  }

  /**
   * ### vue lifecycle mounted
   */
  public async mounted(): Promise<void> {
    // 画面初期表示時に作成して変更しないものを初期化する
    const setting: OutingSettingGetModel = await this.getOutingSetting();
    this.setting = setting;
    const fields: Array<NeedsMealsTableField> = this.createFields(setting);
    this.fields = fields;
    // 1ページあたりの表示行数はCookieから取得する。取得できない場合はデフォルト値を使用する
    const perPage: number | null = this.loadCookie();
    this.perPage = perPage ? perPage : Number(Setting.room.perPage);

    const groups: Array<ResidentGroupsGetModel> = await this.getResidentGroups();
    const groupIdMap: Map<number, ResidentGroupsGetModel> = this.createGroupIdMap(groups);
    this.groupIdMap = groupIdMap;
    const filterOptions: Array<{ value: string; text: string }> = await this.createFilterOptions(groups);
    this.filterOptions = filterOptions;
    // フィルタの復元
    this.restoreFilterSetting();

    await this.createView();
    return;
  }

  /**
   * ### 外出外泊届け設定取得
   */
  public async getOutingSetting(): Promise<OutingSettingGetModel> {
    const response: OutingSettingGetModel = await this.outingSettingsService.get(userStore);
    return response;
  }

  /**
   * ### テーブル列名を作成する
   */
  public createFields(setting: OutingSettingGetModel): Array<NeedsMealsTableField> {
    const fields: Array<NeedsMealsTableField> = [];
    fields.push({ key: "date", label: "日", sortable: false, sortDirection: "desc", class: "date" });

    // 食事列は設定に応じて表示／非表示を切り替える
    if (setting.useNeedsMeal.breakfast) {
      fields.push({ key: "breakfast", label: "朝食", sortable: false, sortDirection: "desc", class: "meal-name breakfast text-center" });
    }
    if (setting.useNeedsMeal.lunch) {
      fields.push({ key: "lunch", label: "昼食", sortable: false, sortDirection: "desc", class: "meal-name lunch text-center" });
    }
    if (setting.useNeedsMeal.snack) {
      fields.push({ key: "snack", label: "おやつ", sortable: false, sortDirection: "desc", class: "meal-name snack text-center" });
    }
    if (setting.useNeedsMeal.dinner) {
      fields.push({ key: "dinner", label: "夕食", sortable: false, sortDirection: "desc", class: "meal-name dinner text-center" });
    }

    fields.push({ key: "list", label: "一覧", sortable: false, sortDirection: "desc", class: "list text-center" });
    return fields;
  }

  /**
   * ### Cookieを見て、値が保持されている場合はCookieの値を返す
   */
  public loadCookie(): number | null {
    if (document.cookie) {
      let cookieMap: any = {};
      let cookies: Array<string> = document.cookie.split("; ");
      cookies.forEach(function (value) {
        //cookie名と値に分ける
        let content = value.split("=");
        cookieMap[content[0]] = content[1];
      });
      // cookieに値が格納されていないとundefinedが格納されているが、実際にはゴミデータが格納されているのでisNaN()でチェックが必要
      const perPage: number = Number(cookieMap["needs-meals-list-per-page"]);
      return isNaN(perPage) ? null : perPage;
    }

    return null;
  }

  /**
   * ### 全ての利用者グループを取得する
   */
  public async getResidentGroups(): Promise<Array<ResidentGroupsGetModel>> {
    const response: Array<ResidentGroupsGetModel> = await this.residentGroupsService.gets(userStore);
    return response;
  }

  /**
   * ### 利用者グループマスターのMAPを作る
   */
  public createGroupIdMap(residentGroups: Array<ResidentGroupsGetModel>): Map<number, ResidentGroupsGetModel> {
    const map: Map<number, ResidentGroupsGetModel> = new Map<number, ResidentGroupsGetModel>();
    for (const group of residentGroups) {
      map.set(group.id, group);
    }
    return map;
  }

  /**
   * ### 利用者グループフィルター用選択項目
   *
   * - アカウント情報から許可されている利用者グループIDの配列を取得する
   * - 利用者グループマスターと照合して選択項目の利用者グループを作成する
   */
  public createFilterOptions(residentGroups: Array<ResidentGroupsGetModel>): Array<{ value: string; text: string }> {
    const filterOptions: Array<{ value: string; text: string }> = [];

    filterOptions.push({
      value: "",
      text: "全て"
    });

    const allowedGroupsMap: Map<number, number> = new Map<number, number>();
    for (const allowedGroup of userStore.loginUser.permissions.residentGroups) {
      allowedGroupsMap.set(allowedGroup, allowedGroup);
    }
    for (const group of residentGroups) {
      if (allowedGroupsMap.has(group.id)) {
        filterOptions.push({
          value: `${group.id}`,
          text: group.name
        });
      }
    }

    return filterOptions;
  }

  /** フィルタ情報がstoreに存在する場合は復元する */
  private restoreFilterSetting(): void {
    if (this.$options.name && itemListFilterStore.filterMap[this.$options.name] != undefined) {
      this.filter = itemListFilterStore.filterMap[this.$options.name];
    }
    return;
  }

  /**
   * ### 画面情報作成
   */
  public async createView(): Promise<void> {
    this.isLoading = true;

    // 現在より未来の外出外泊届けを取得する
    // 開始日時は今日の0時、終了日時は17桁の90000101000000000でリクエストする
    const startDatetime: number = this.$dateUtil.convertDateToNumber(new Date(), "yyyymmdd000000000");
    const endDatetime: number = 90000101000000000;

    // 全体の行数を非同期で取得する
    this.setRows(startDatetime, endDatetime);

    // テーブルに渡す各プロパティを作成する
    const outings: Array<OutingNeedsMealsGetModel> = await this.getNeedsMeals(startDatetime, endDatetime);
    const items: Array<NeedsMealsTableItem> = this.createItems(outings);
    this.rows = this.items.length;
    this.items = items;

    this.isLoading = false;

    return;
  }

  /**
   * ### ページング用の全件数を設定する
   */
  public async setRows(start: number, end: number) {
    try {
      const count: number = await this.outingsService.getCount(userStore, { start, end });
      this.rows = count;
    } catch (error) {
      // 本処理はawaitされない非同期処理なので、共通エラーハンドラーが例外をキャッチしない
      // 例外発生時は自分でエラーハンドラーを呼ぶ
      errorHandle(error, this);
    }
    return;
  }

  /**
   * ### 食事キャンセル情報を取得する
   */
  public async getNeedsMeals(start: number, end: number): Promise<Array<OutingNeedsMealsGetModel>> {
    let response: Array<OutingNeedsMealsGetModel> = [];

    try {
      response = await this.outingsService.getsNeedsMeals(userStore, { start, end });
    } catch (error) {
      const systemerror: SystemError = error as SystemError;
      // 413エラーの場合、全件取得した結果がペイロード制限に引っかかったことになるので、index指定で段階的に取得する
      if (systemerror.statusCode == 413) {
        const count: number = await this.outingsService.getCount(userStore, { start, end });
        const loopEnd: number = Math.ceil(count / 300);

        for (let i = 0; i < loopEnd; i++) {
          const params: { start: number; end: number; startIndex?: number; endIndex?: number } = {
            start,
            end,
            startIndex: i * 300,
            endIndex: i * 300 + 300
          };
          response = response.concat(await this.outingsService.getsNeedsMeals(userStore, params));
        }
      }
    }
    return response;
  }

  /**
   * ### テーブル行を作成する
   */
  public createItems(needsMealsByDate: Array<OutingNeedsMealsGetModel>): Array<NeedsMealsTableItem> {
    const items: Array<NeedsMealsTableItem> = [];
    for (const outing of needsMealsByDate) {
      const tableItemNeedsMeals: Array<TableItemNeedsMeal> = [];
      for (const needsMeal of outing.needsMeals) {
        tableItemNeedsMeals.push({
          ...needsMeal,
          countTarget: true
        });
      }
      items.push({
        ...outing,
        needsMeals: tableItemNeedsMeals
      });
    }
    return items;
  }

  /**
   * ### 1ページあたりの表示行数変更イベント
   *
   * - 1ページあたりの表示行数を更新
   */
  public async selectPerPage(perPage: number): Promise<void> {
    this.perPage = perPage;
    this.currentPage = 1;
    return;
  }

  /**
   * ### ページネーションのページ番号押下
   */
  public async selectPage(page: number): Promise<void> {
    this.currentPage = page;
    return;
  }

  /**
   * ### 各行の食事キャンセル情報の「カウント対象かどうか」を更新する
   *
   * - 利用者グループIDによるフィルタリング実施後に呼ばれる
   * - 現在されている利用者グループIDを見て、そのグループIDであれば表示対象として扱う
   * - 利用者グループが「全て」の場合、全てを表示対象とする
   * - NeedsMealsTable.filterGroupId()で実施した方が効率が良いが、関数の責務を分けたいのでここで実施する
   */
  public updateCountTargetByGroupId(): void {
    if (this.filter.id == "") {
      for (const item of this.items) {
        for (const needsMeal of item.needsMeals) {
          needsMeal.countTarget = true;
        }
      }
      return;
    }

    for (const item of this.items) {
      for (const needsMeal of item.needsMeals) {
        if (needsMeal.residentGroupId == Number(this.filter.id)) {
          needsMeal.countTarget = true;
        } else {
          needsMeal.countTarget = false;
        }
      }
    }
    return;
  }

  /**
   * ### 食事キャンセル詳細ボタン押下イベント
   */
  public async onDetail(target: { item: NeedsMealsTableItem; mealType: MealType }): Promise<void> {
    this.needsMealModalDatetime = target.item.datetime;
    this.needsMealModalMealType = target.mealType;

    const items: Array<NeedsMealModalTableItem> = [];
    for (const needsMeal of target.item.needsMeals) {
      const group: ResidentGroupsGetModel | undefined = this.groupIdMap.get(needsMeal.residentGroupId);
      items.push({
        ...needsMeal,
        residentGroupName: group ? group.name : ""
      });
    }
    this.needsMealModalItems = items;

    this.showMealModal = true;
    return;
  }

  /**
   * ### 一覧詳細ボタン押下イベント
   */
  public async onListDetail(item: NeedsMealsTableItem): Promise<void> {
    this.needsMealsModalDatetime = item.datetime;

    const items: Array<NeedsMealModalTableItem> = [];
    for (const needsMeal of item.needsMeals) {
      const group: ResidentGroupsGetModel | undefined = this.groupIdMap.get(needsMeal.residentGroupId);
      items.push({
        ...needsMeal,
        residentGroupName: group ? group.name : ""
      });
    }
    this.needsMealsModalItems = items;

    this.showMealsModal = true;
    return;
  }

  setFilterSetting() {
    // 画面を離れる前に現在のフィルタリング条件をVuexに保存
    if (this.$options.name) {
      itemListFilterStore.setFilter({ url: this.$options.name, filter: this.filter });
    }
    return;
  }
}
</script>

<style lang="scss" scoped>
.needs-meals-tab {
  .word-search-filter {
    min-width: 336px;
  }
  .resident-group-filter {
    min-width: 295px;
  }
  .table-option-text {
    min-width: 113px;
  }
}
</style>
